/**
 * ===========================================================================
 *  MINIMAP
 * ===========================================================================
 *
 * Reading a Dynasty Warriors battlefield is half map work: where the fronts
 * are, which base is about to flip, which way the officer you want is. So this
 * is a real map, not a decoration.
 *
 * TWO LAYERS
 *  1. A STATIC relief, baked once at init. The colour comes from the terrain's
 *     own baked macro albedo (`world.terrain.macroTex.image.data`, 1024^2
 *     RGBA), so roads, river sand, trodden dirt and grass are exactly the
 *     colours you see on the ground. It is then hill-shaded from a height grid
 *     sampled through `terrain.heightAt`, pushed toward an inked, desaturated
 *     campaign-map palette and given water, a rim vignette and a grid.
 *  2. A LIVE pass each frame: base markers, every live unit as a faction blip,
 *     the engaged officer ringed, and the player arrow with the camera cone.
 *
 * COST CONTROL
 *  - The live canvas has a FIXED 384x384 backing store no matter the display
 *    resolution, so a 4K HUD costs exactly what a 720p one costs.
 *  - Blips are batched into two paths (one per faction) and filled twice — not
 *    600 fill calls.
 *  - No allocation: colour strings are constants, the static layer is a canvas.
 */
import { FACTION, HAS_RIVER } from '../world/layout.js';
import { WALLS } from '../world/walls.js';
import { factionOf, SIDE, ORNAMENT } from '../core/theme.js';

export const MAPRES = 384;

/* The map's own read of each side. Deliberately a separate surface from the
 * HUD's `--ally` / `--enemy`: a 3 px marker on a baked relief is a different
 * legibility problem from a bar on a dark panel. See core/theme.js. */
const A = factionOf(SIDE.PLAYER), E = factionOf(SIDE.ENEMY), N = factionOf(SIDE.NEUTRAL);
const COL = {
  ally: A.map.main, allyDim: A.map.dim,
  enemy: E.map.main, enemyDim: E.map.dim,
  neutral: N.map.main,
  gold: ORNAMENT.gold,
};

/** Faction hex -> css. */
function fc(team, key) {
  const f = FACTION[team % FACTION.length] || FACTION[2];
  return '#' + (f[key] >>> 0).toString(16).padStart(6, '0');
}

export function createMinimap() {
  const cv = document.createElement('canvas');
  cv.width = cv.height = MAPRES;
  const ctx = cv.getContext('2d');
  const base = document.createElement('canvas');
  base.width = base.height = MAPRES;
  const bctx = base.getContext('2d');

  let built = false, half = 260, size = 520;
  let W = null;

  /* ------------------------------------------------------------- the bake */

  function build(w) {
    W = w;
    const T = w.terrain;
    if (!T) return false;
    half = T.half || 260; size = T.size || half * 2;

    const N = MAPRES;
    const img = bctx.createImageData(N, N);
    const px = img.data;

    // height grid first — one heightAt per pixel, then finite-difference it
    const H = new Float32Array(N * N);
    for (let j = 0; j < N; j++) {
      const z = -half + ((j + 0.5) / N) * size;
      for (let i = 0; i < N; i++) {
        const x = -half + ((i + 0.5) / N) * size;
        H[j * N + i] = T.heightAt(x, z);
      }
    }
    let hMin = 1e9, hMax = -1e9;
    for (let i = 0; i < H.length; i++) { if (H[i] < hMin) hMin = H[i]; if (H[i] > hMax) hMax = H[i]; }
    const hRange = Math.max(1, hMax - hMin);

    // macro albedo, if terrain published it
    const macro = T.macroTex && T.macroTex.image && T.macroTex.image.data ? T.macroTex.image.data : null;
    const MR = macro ? Math.round(Math.sqrt(macro.length / 4)) : 0;

    const step = size / N;
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        const k = j * N + i;
        let r, g, b;
        if (macro) {
          const mi = Math.min(MR - 1, (i * MR / N) | 0);
          const mj = Math.min(MR - 1, (j * MR / N) | 0);
          const m4 = (mj * MR + mi) * 4;
          r = macro[m4]; g = macro[m4 + 1]; b = macro[m4 + 2];
        } else {
          const mat = T.materialAt ? T.materialAt(-half + (i + 0.5) * step, -half + (j + 0.5) * step) : 0;
          const p = [[86, 104, 58], [104, 86, 62], [126, 116, 100], [122, 122, 126], [178, 158, 116]][mat] || [86, 104, 58];
          r = p[0]; g = p[1]; b = p[2];
        }

        // hill shade: sun from the north-west, matching the scene key
        const hl = H[j * N + Math.max(0, i - 1)], hr = H[j * N + Math.min(N - 1, i + 1)];
        const hu = H[Math.max(0, j - 1) * N + i], hd = H[Math.min(N - 1, j + 1) * N + i];
        const nx = (hl - hr) / (2 * step), nz = (hu - hd) / (2 * step);
        const lit = Math.max(0.30, Math.min(1.9, 0.94 + nx * 1.35 + nz * 1.00));
        const alt = (H[k] - hMin) / hRange;

        // ink the map: desaturate hard, keep a warm/cool altitude ramp
        const lum = r * 0.30 + g * 0.58 + b * 0.12;
        const sat = 0.44;
        r = lum + (r - lum) * sat; g = lum + (g - lum) * sat; b = lum + (b - lum) * sat;
        const warm = 0.86 + alt * 0.62;
        r = 16 + r * lit * warm * 1.24;
        g = 15 + g * lit * (0.82 + alt * 0.52) * 1.24;
        b = 14 + b * lit * (0.66 + alt * 0.40) * 1.24;

        // low ground reads as water/marsh in the river cut — on an arena that
        // HAS a river. On made ground the lowest 7.5 % of the range is most of
        // the map, and the kraton's first plate came out as a blue marsh.
        if (HAS_RIVER && alt < 0.075) {
          const t = 1 - alt / 0.075;
          r = r * (1 - t) + 34 * t; g = g * (1 - t) + 76 * t; b = b * (1 - t) + 108 * t;
        }

        // rim falloff so the plate has a dark edge to sit the frame on
        const dx = (i / N - 0.5) * 2, dy = (j / N - 0.5) * 2;
        const rr = Math.min(1, Math.hypot(dx, dy) / 1.02);
        const vg = 1 - Math.pow(rr, 3.6) * 0.46;
        r *= vg; g *= vg; b *= vg;

        const o = k * 4;
        px[o] = r < 0 ? 0 : r > 255 ? 255 : r;
        px[o + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
        px[o + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
        px[o + 3] = 255;
      }
    }
    bctx.putImageData(img, 0, 0);

    // survey grid over the relief
    bctx.save();
    bctx.globalAlpha = 0.13;
    bctx.strokeStyle = '#f4e2b8';
    bctx.lineWidth = 1;
    for (let i = 1; i < 8; i++) {
      const p = Math.round((i / 8) * N) + 0.5;
      bctx.beginPath(); bctx.moveTo(p, 0); bctx.lineTo(p, N); bctx.stroke();
      bctx.beginPath(); bctx.moveTo(0, p); bctx.lineTo(N, p); bctx.stroke();
    }
    bctx.restore();

    // base footprints are part of the static plate — they never move
    const bases = w.bases && w.bases.list;
    if (bases) {
      for (let i = 0; i < bases.length; i++) {
        const b = bases[i];
        const mx = ((b.x + half) / size) * N, my = ((b.z + half) / size) * N;
        const mr = Math.max(4, (b.radius / size) * N);
        bctx.save();
        bctx.globalAlpha = 0.30;
        const gg = bctx.createRadialGradient(mx, my, mr * 0.15, mx, my, mr * 1.5);
        gg.addColorStop(0, 'rgba(255,232,190,.65)');
        gg.addColorStop(1, 'rgba(255,232,190,0)');
        bctx.fillStyle = gg;
        bctx.beginPath(); bctx.arc(mx, my, mr * 1.5, 0, Math.PI * 2); bctx.fill();
        bctx.restore();
      }
    }

    // the arena's walls are part of the plate too: brick ink, gaps at gates
    if (WALLS.n) {
      bctx.save();
      bctx.strokeStyle = 'rgba(62,26,16,.92)';
      bctx.lineCap = 'butt';
      for (const r of WALLS.runs) {
        bctx.lineWidth = Math.max(1.4, (r.t0 / size) * N * 1.4);
        bctx.beginPath();
        bctx.moveTo(((r.x0 + half) / size) * N, ((r.z0 + half) / size) * N);
        bctx.lineTo(((r.x1 + half) / size) * N, ((r.z1 + half) / size) * N);
        bctx.stroke();
      }
      bctx.restore();
    }

    built = true;
    return true;
  }

  /* -------------------------------------------------------------- markers */

  function marker(g, b, N) {
    const x = ((b.x + half) / size) * N, y = ((b.z + half) / size) * N;
    const team = b.team | 0;
    const col = team === 0 ? COL.ally : team === 1 ? COL.enemy : COL.neutral;
    const s = b.type === 'fort' ? 8.5 : b.type === 'camp' ? 7 : 5.8;

    g.save();
    g.translate(x, y);
    g.lineWidth = 1.6;
    g.strokeStyle = 'rgba(6,4,3,.9)';
    g.fillStyle = col;

    if (b.type === 'fort') {
      // battlemented square
      g.beginPath();
      g.moveTo(-s, -s); g.lineTo(-s * 0.35, -s); g.lineTo(-s * 0.35, -s * 0.6);
      g.lineTo(s * 0.35, -s * 0.6); g.lineTo(s * 0.35, -s); g.lineTo(s, -s);
      g.lineTo(s, s); g.lineTo(-s, s); g.closePath();
    } else if (b.type === 'camp') {
      g.beginPath();                                  // tent
      g.moveTo(0, -s); g.lineTo(s, s * 0.75); g.lineTo(-s, s * 0.75); g.closePath();
    } else if (b.type === 'bridge') {
      g.beginPath();
      g.moveTo(-s, s * 0.4); g.lineTo(-s, -s * 0.1);
      g.quadraticCurveTo(0, -s * 1.1, s, -s * 0.1); g.lineTo(s, s * 0.4);
      g.lineTo(s * 0.5, s * 0.4); g.lineTo(s * 0.5, s * 0.05);
      g.quadraticCurveTo(0, -s * 0.6, -s * 0.5, s * 0.05); g.lineTo(-s * 0.5, s * 0.4);
      g.closePath();
    } else {
      g.beginPath();                                  // watch post: diamond
      g.moveTo(0, -s); g.lineTo(s * 0.8, 0); g.lineTo(0, s); g.lineTo(-s * 0.8, 0); g.closePath();
    }
    g.fill(); g.stroke();

    // tiny faction pennant on top
    g.fillStyle = fc(team, 'trim');
    g.fillRect(-0.7, -s - 5.5, 1.4, 5.5);
    g.fillStyle = fc(team, 'cloth');
    g.beginPath();
    g.moveTo(0.7, -s - 5.5); g.lineTo(5.4, -s - 4.1); g.lineTo(0.7, -s - 2.7); g.closePath();
    g.fill();
    g.restore();
  }

  /* ------------------------------------------------------------- the frame */

  function frame(g, N) {
    const c = N * 0.075;
    g.save();
    g.beginPath();
    g.moveTo(c, 0); g.lineTo(N - c, 0); g.lineTo(N, c); g.lineTo(N, N - c);
    g.lineTo(N - c, N); g.lineTo(c, N); g.lineTo(0, N - c); g.lineTo(0, c);
    g.closePath();
    g.lineWidth = N * 0.030;
    g.strokeStyle = 'rgba(6,5,4,.95)';
    g.stroke();
    const gr = g.createLinearGradient(0, 0, N, N);
    gr.addColorStop(0, '#f6dfa8'); gr.addColorStop(0.42, '#a17c2c');
    gr.addColorStop(0.68, '#5c431a'); gr.addColorStop(1, '#e6c581');
    g.lineWidth = N * 0.012;
    g.strokeStyle = gr;
    g.stroke();

    // compass N
    g.fillStyle = 'rgba(250,230,186,.9)';
    g.beginPath();
    g.moveTo(N * 0.5, N * 0.030); g.lineTo(N * 0.523, N * 0.075);
    g.lineTo(N * 0.5, N * 0.062); g.lineTo(N * 0.477, N * 0.075);
    g.closePath(); g.fill();
    g.restore();
  }

  /* ---------------------------------------------------------------- render */

  function draw(w) {
    if (!built) return;
    const N = MAPRES;
    const e = w.entities;
    ctx.clearRect(0, 0, N, N);

    const c = N * 0.075;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(c, 0); ctx.lineTo(N - c, 0); ctx.lineTo(N, c); ctx.lineTo(N, N - c);
    ctx.lineTo(N - c, N); ctx.lineTo(c, N); ctx.lineTo(0, N - c); ctx.lineTo(0, c);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(base, 0, 0);

    const k = N / size;
    const pid = w.playerId;

    /* --- unit blips, batched per faction -------------------------------- */
    const live = e.live, n = e.liveN;
    for (let pass = 0; pass < 2; pass++) {
      ctx.beginPath();
      let drew = 0;
      for (let i = 0; i < n; i++) {
        const id = live[i];
        if (!e.alive[id] || id === pid) continue;
        if (e.hp[id] <= 0) continue;
        const t = e.team[id];
        if ((pass === 0) !== (t === 0)) continue;
        const mx = (e.x[id] + half) * k, my = (e.z[id] + half) * k;
        if (mx < -2 || my < -2 || mx > N + 2 || my > N + 2) continue;
        ctx.rect(mx - 1.2, my - 1.2, 2.4, 2.4);
        drew = 1;
      }
      if (drew) {
        ctx.fillStyle = pass === 0 ? COL.allyDim : COL.enemyDim;
        ctx.fill();
      }
    }

    /* --- named officers get a bigger lozenge ----------------------------- */
    const offs = w.ai && w.ai.officers;
    if (offs) {
      for (let i = 0; i < offs.length; i++) {
        const o = offs[i];
        if (!o.alive) continue;
        const id = o.id;
        if (id < 0 || !e.alive[id]) continue;
        const mx = (e.x[id] + half) * k, my = (e.z[id] + half) * k;
        ctx.save();
        ctx.translate(mx, my);
        ctx.rotate(Math.PI * 0.25);
        ctx.fillStyle = o.team === 0 ? COL.ally : COL.enemy;
        ctx.strokeStyle = 'rgba(4,3,2,.9)';
        ctx.lineWidth = 1.4;
        ctx.fillRect(-3.1, -3.1, 6.2, 6.2);
        ctx.strokeRect(-3.1, -3.1, 6.2, 6.2);
        ctx.restore();
      }
    }
    const eng = w.ai && w.ai.engaged;
    if (eng && eng.alive && eng.id >= 0 && e.alive[eng.id]) {
      const mx = (e.x[eng.id] + half) * k, my = (e.z[eng.id] + half) * k;
      ctx.strokeStyle = '#ffd98a';
      ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.arc(mx, my, 8, 0, Math.PI * 2); ctx.stroke();
    }

    /* --- bases ----------------------------------------------------------- */
    const bases = w.bases && w.bases.list;
    if (bases) for (let i = 0; i < bases.length; i++) marker(ctx, bases[i], N);

    /* --- player: camera cone + arrow ------------------------------------- */
    if (pid >= 0 && e.alive[pid]) {
      const mx = (e.x[pid] + half) * k, my = (e.z[pid] + half) * k;
      // The cone draws where the camera is LOOKING, so it reads the solved
      // pose, not the base one. `camrig.yaw` is the rig's resting yaw; a musou
      // orbit, a cinematic or the opening sweep adds up to a full turn on top of
      // it and only writes it back when the move ends, so drawing from the base
      // pointed the cone the wrong way for the whole of a musou. Movement still
      // takes its axes from the base yaw (src/game/player.js) on purpose — the
      // controls should not spin with an orbit the player did not ask for.
      const cyaw = w.camrig ? (w.camrig.solved?.yaw ?? w.camrig.yaw) : e.yaw[pid];
      ctx.save();
      ctx.translate(mx, my);
      ctx.rotate(-cyaw);
      const R = N * 0.16, fov = 0.52;
      const cg = ctx.createRadialGradient(0, 0, 2, 0, 0, R);
      cg.addColorStop(0, 'rgba(255,238,200,.42)');
      cg.addColorStop(1, 'rgba(255,238,200,0)');
      ctx.fillStyle = cg;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, R, Math.PI / 2 - fov, Math.PI / 2 + fov);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      ctx.save();
      ctx.translate(mx, my);
      ctx.rotate(-e.yaw[pid]);
      ctx.beginPath();
      ctx.moveTo(0, 7.6); ctx.lineTo(-5.2, -5.4); ctx.lineTo(0, -2.6); ctx.lineTo(5.2, -5.4);
      ctx.closePath();
      ctx.fillStyle = '#fff3d4';
      ctx.strokeStyle = 'rgba(20,10,4,.95)';
      ctx.lineWidth = 1.8;
      ctx.stroke(); ctx.fill();
      ctx.restore();
    }
    ctx.restore();

    frame(ctx, N);
  }

  return {
    canvas: cv, build, draw,
    /** The baked static relief, for anything that wants the map without the
     *  live markers on it — the battlefield-select plate draws this. */
    relief: base,
    get half() { return half; },
    get size() { return size; },
    get built() { return built; },
    /** world -> map pixel, for anything else that wants to plot on it */
    toMap(x, z, out) { out.x = ((x + half) / size) * MAPRES; out.y = ((z + half) / size) * MAPRES; return out; },
  };
}
